import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import NotificationCenter from '../../components/ui/NotificationCenter';
import Icon from '../../components/AppIcon';
import CategoryTree from './components/CategoryTree';
import CategoryDetailPanel from './components/CategoryDetailPanel';
import CategoryFormModal from './components/CategoryFormModal';
import { materialCategoryService } from '../../services/materialCategoryService';

const MaterialCategoryManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    const { data: allCategories, error: categoriesError } = await materialCategoryService?.getAllCategories();
    const { data: tree, error: treeError } = await materialCategoryService?.getCategoryTree();

    if (categoriesError || treeError) {
      alert('Error loading categories: ' + (categoriesError || treeError));
    } else {
      setCategories(allCategories || []);
      setCategoryTree(tree || []);
    }
    setLoading(false);
  };

  const handleCreateCategory = async (categoryData) => {
    const { error } = await materialCategoryService?.createCategory(categoryData);
    if (error) {
      alert('Error creating category: ' + error);
    } else {
      setShowCreateModal(false);
      loadCategories();
    }
  };

  const handleUpdateCategory = async (categoryData) => {
    const { error } = await materialCategoryService?.updateCategory(editingCategory?.id, categoryData);
    if (error) {
      alert('Error updating category: ' + error);
    } else {
      setEditingCategory(null);
      loadCategories();
      if (selectedCategory?.id === editingCategory?.id) {
        setSelectedCategory({ ...selectedCategory, ...categoryData });
      }
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category? All subcategories will also be deleted.')) {
      return;
    }

    const { error } = await materialCategoryService?.deleteCategory(categoryId);
    if (error) {
      alert('Error deleting category: ' + error);
    } else {
      if (selectedCategory?.id === categoryId) {
        setSelectedCategory(null);
      }
      loadCategories();
    }
  };

  const handleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => {
      if (prev?.includes(categoryId)) {
        return prev?.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const sortCategoriesRecursive = (categories, order) => {
    if (!categories || categories?.length === 0) return categories;
    
    const sorted = [...categories]?.sort((a, b) => {
      const nameA = a?.name?.toLowerCase() || '';
      const nameB = b?.name?.toLowerCase() || '';
      return order === 'asc' 
        ? nameA?.localeCompare(nameB)
        : nameB?.localeCompare(nameA);
    });

    return sorted?.map(category => ({
      ...category,
      children: sortCategoriesRecursive(category?.children, order)
    }));
  };

  const filteredTree = categoryTree?.filter(category => 
    category?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const sortedTree = sortCategoriesRecursive(filteredTree, sortOrder);

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} />
      <QuickActionsToolbar />
      <div className={`transition-all duration-250 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center gap-4">
              <button
                className="hidden lg:flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                aria-label="Toggle sidebar"
              >
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                  Material Category Management
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Manage hierarchical material categories up to 5 levels deep
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter />
              <button
                onClick={() => {
                  setEditingCategory(null);
                  setShowCreateModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-250"
              >
                <Icon name="Plus" size={18} />
                <span className="hidden md:inline">Create Category</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex h-[calc(100vh-120px)]">
          {/* Category Tree - 60% */}
          <div className="w-[60%] border-r border-border">
            <div className="p-4 border-b border-border space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Icon
                    name="Search"
                    size={16}
                    color="var(--color-muted-foreground)"
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                  />
                  <input
                    type="text"
                    placeholder="Search categories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <button
                  onClick={toggleSortOrder}
                  className="flex items-center gap-2 px-3 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors duration-250 whitespace-nowrap"
                  title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
                >
                  <Icon name={sortOrder === 'asc' ? 'ArrowUpAZ' : 'ArrowDownZA'} size={18} />
                  <span className="text-sm font-medium">Name</span>
                </button>
              </div>
            </div>
            <div className="overflow-y-auto h-[calc(100%-80px)]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader2" size={32} className="animate-spin" color="var(--color-primary)" />
                </div>
              ) : (
                <CategoryTree
                  categories={sortedTree}
                  selectedCategory={selectedCategory}
                  expandedCategories={expandedCategories}
                  onCategorySelect={setSelectedCategory}
                  onCategoryExpand={handleCategoryExpand}
                  onCategoryEdit={(category) => {
                    setEditingCategory(category);
                    setShowCreateModal(true);
                  }}
                  onCategoryDelete={handleDeleteCategory}
                />
              )}
            </div>
          </div>

          {/* Category Detail Panel - 40% */}
          <div className="w-[40%]">
            <CategoryDetailPanel
              category={selectedCategory}
              onEdit={(category) => {
                setEditingCategory(category);
                setShowCreateModal(true);
              }}
              onDelete={handleDeleteCategory}
            />
          </div>
        </main>
      </div>

      {/* Category Form Modal */}
      {showCreateModal && (
        <CategoryFormModal
          onClose={() => {
            setShowCreateModal(false);
            setEditingCategory(null);
          }}
          onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory}
          editingCategory={editingCategory}
          categories={categories}
        />
      )}
    </div>
  );
};

export default MaterialCategoryManagement;
